import { API, graphqlOperation } from "aws-amplify";
import { logError } from "./appUtils";
import { zapByActiveByEvent } from "../graphql/reloadyard/custom-queries";
import get from "lodash/get";
import set from "lodash/set";

/**
 * Send an email using the SES endpoint.
 *
 * @param {Object} emailBody - Email payload containing `to`, `subject`, `cc`, and `htmlContent`.
 * @param {string} logPath - Path for logging errors.
 * @param {Function} [notify] - Optional notification callback. Called with (message, type).
 */
const sesSendEmail = (emailBody, logPath, notify) => {
  API.post("transREST", "/sendEmail", {
    body: emailBody,
  })
    .then(() => {
      if (notify) notify("Email sent.", "success");
    })
    .catch((error) => {
      if (notify) notify("Error sending email.", "error");
      // Log the error without crashing the UI
      logError(logPath + ":sesSendEmail", error, false);
    });
};

/**
 * Send an email, either directly via SES or through a Zapier trigger if configured.
 *
 * @param {Object} emailBody - Email payload containing `to`, `subject`, `cc`, and `htmlContent`.
 * @param {string} logPath - Path for logging errors.
 * @param {Function} [notify] - Optional notification callback. Called with (message, type).
 * @param {Object} [optionsOverride] - Optional Zapier request override options.
 * @returns {Promise<any>} The API response or undefined.
 */
export const sendEmail = async (
  emailBody,
  logPath,
  notify,
  optionsOverride
) => {
  let response;

  if (window.appUser.appType === "CUSTOMER") {
    sesSendEmail(emailBody, logPath, notify);
  } else {
    const hasSendEmailHook = await fetchTriggerEnabled("newEmail");

    if (hasSendEmailHook) {
      API.post("zapierREST", "/handleTrigger", {
        body: {
          quary: {
            to: emailBody.to,
            subject: emailBody.subject,
            cc: emailBody.cc || undefined,
            htmlBody: emailBody.htmlContent,
          },
          optionsOverride: optionsOverride || {},
          reloadYardId: window.appUser.reloadYard.id,
          event: "newEmail",
        },
      })
        .then(() => {
          if (notify) notify("Email sent.", "success");
        })
        .catch((error) => {
          if (notify) notify("Error sending email.", "error");
          logError(logPath + ":sendEmail", error, false);
        });
    } else {
      sesSendEmail(emailBody, logPath, notify);
    }
  }

  return response;
};

/**
 * Trigger a custom event in Zapier.
 *
 * @param {any} data - Event payload to send.
 * @param {string} event - Event name.
 * @param {Object} [optionsOverride] - Optional Zapier request override options.
 * @returns {Promise<void>}
 */
export const triggerEvent = async (data, event, optionsOverride) => {
  await API.post("zapierREST", "/handleTrigger", {
    body: {
      quary: data,
      optionsOverride: optionsOverride || {},
      reloadYardId: window.appUser.reloadYardId,
      event,
    },
  });
};

/** @private */
function key(reloadYardId, event) {
  return `zap:isTriggerEnabled:${reloadYardId}:${event}`;
}

/** @private */
function readCache(reloadYardId, event) {
  try {
    const raw = sessionStorage.getItem(key(reloadYardId, event));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.value === "boolean" ? parsed.value : null;
  } catch {
    return null;
  }
}

/** @private */
function writeCache(reloadYardId, event, value) {
  try {
    sessionStorage.setItem(
      key(reloadYardId, event),
      JSON.stringify({ value: !!value })
    );
  } catch {
    // ignore storage errors
  }
}

/**
 * Check if a trigger is enabled using cache only.
 *
 * @param {string} event - Event name to check.
 * @returns {boolean} Whether the event is enabled.
 */
export const getTriggerEnabled = (event) => {
  if (!event) return false;
  const reloadYardId = window?.appUser?.reloadYard?.id;
  if (!reloadYardId) return false;
  const cached = readCache(reloadYardId, event);
  return cached ?? false;
};

/**
 * Check if a trigger is enabled, with server validation and cache update.
 *
 * @param {string} event - Event name to check.
 * @returns {Promise<boolean>} Whether the event is enabled.
 */
export const fetchTriggerEnabled = async (event) => {
  if (!event) return false;
  const reloadYardId = window?.appUser?.reloadYard?.id;
  if (!reloadYardId) return false;

  try {
    const res = await API.graphql(
      graphqlOperation(zapByActiveByEvent, {
        reloadYardId,
        activeEvent: {
          beginsWith: { active: "true", event },
        },
      })
    );
    const value = (res?.data?.zapByActiveByEvent?.items?.length || 0) > 0;
    writeCache(reloadYardId, event, value);
    return value;
  } catch {
    // fallback to cached value on network errors
    const cached = readCache(reloadYardId, event);
    return cached ?? false;
  }
};

/**
 * Check if any dirty fields match syncable props.
 *
 * @param {Object} dirtyFieldsObject - Object of dirty fields from form state.
 * @param {Array<{name: string|RegExp, required: boolean}>} syncableProps - Properties to check.
 * @returns {boolean} True if any dirty field is syncable.
 */
export const checkDirtySync = (dirtyFieldsObject, syncableProps) => {
  const dirtyFields = Object.keys(dirtyFieldsObject || {});
  const syncablePropsKeys = syncableProps.map((prop) => prop.name);
  return syncablePropsKeys.some((field) => {
    if (typeof field === "string") {
      return dirtyFields.includes(field);
    } else {
      return field.test(dirtyFields);
    }
  });
};

/**
 * Expand syncable properties that use regex to match nested paths (e.g., line items).
 *
 * @param {Array<{name: string|RegExp, required: boolean}>} syncableProps - Properties to expand.
 * @param {Object} data - Data object containing `lineItems.items`.
 * @returns {Array<{name: string, required: boolean}>} Expanded syncable props with concrete paths.
 */
function expandSyncableProps(syncableProps, data) {
  const expanded = [];
  const items = get(data, "lineItems.items", []);
  const itemsLength =
    Array.isArray(items) && items.length > 0 ? items.length : 1;

  for (const prop of syncableProps) {
    if (typeof prop.name === "string") {
      expanded.push({ name: prop.name, required: prop.required });
    } else if (prop.name instanceof RegExp) {
      // Expand regex against keys of each item
      for (let i = 0; i < itemsLength; i++) {
        const keys = Object.keys(items[i] || {});
        for (const key of keys) {
          let path;
          if (key === "taxRate") {
            path = `lineItems.items[${i}].${key}.taxCode`;
          } else {
            path = `lineItems.items[${i}].${key}`;
          }
          if (prop.name.test(path)) {
            expanded.push({ name: path, required: prop.required });
          }
        }
      }
    }
  }

  return expanded;
}

/**
 * Treat only absent values as missing, so legitimate falsy values like a $0 line item amount pass.
 * @private
 */
const isMissing = (value) =>
  value === undefined || value === null || value === "" || Number.isNaN(value);

/**
 * Sanitize data for Zapier by including only syncable props and enforcing required fields.
 *
 * @param {Array<{name: string|RegExp, required: boolean}>} syncableProps - Properties to include.
 * @param {Object} data - Raw form data.
 * @throws {Error} If required fields are missing.
 * @returns {Object} Sanitized data object containing only allowed properties.
 */
export const sanitizeZapierData = (syncableProps, data) => {
  if (!data) throw new Error("Data is not valid for Zapier");
  const expanded = expandSyncableProps(syncableProps, data);

  const result = {};

  for (const { name, required } of expanded) {
    const value = get(data, name);
    if (required && isMissing(value)) {
      throw new Error(`Data invalid for Zapier: ${name} is required`);
    }
    if (value !== undefined) {
      set(result, name, value);
    }
  }

  return result;
};

export const addServiceNames = (data, services) => {
  const list = Array.isArray(services) ? services : services?.items || [];
  const nameById = new Map(list.map((s) => [s.id, s.name]));
  data.lineItems?.items?.forEach((item) => {
    const name = nameById.get(item.serviceId) || item.service?.name;
    if (name) {
      item.description = name;
      item.serviceName = name;
    }
    delete item.serviceId;
  });
};

/**
 * Prepare invoice data for Zapier.
 *
 * @param {Object} data - Invoice data with `lineItems.items`.
 * @param {Array|{items: Array}} [services] - Account services, used to add `description` and `serviceName` to each line item.
 */
export const transformZapierInvoiceData = (data, services) => {
  addServiceNames(data, services);
  const ast = window.appUser?.reloadYard?.ast;
  if (!ast) {
    data.taxCode = data.lineItems?.items[0]?.taxRate?.taxCode;
    data.lineItems?.items?.forEach((item) => {
      item.tax = item.taxRate?.taxCode;
      delete item.taxRate;
    });
    return data;
  }
  return data;
};

export const hasExternalId = (record) => {
  return record.externalId && record.externalId.trim() !== "";
};

export const hasExternalStatus = (record) => {
  return !!record.externalStatus;
};
